import { z } from 'zod';
import {
  enrichableFieldSchema,
  severitySchema,
  suggestionStatusSchema,
  type SuggestionStatus,
} from '../domain/enums';
import type { Finding } from '../domain/finding';
import type { ProductSnapshotData } from '../domain/snapshot';
import type { Suggestion } from '../domain/suggestion';
import { paginationQuerySchema } from './common';

/** Aceita um status, CSV (`approved,applied`) ou array repetido na query. */
function preprocessStatusList(value: unknown): unknown {
  if (value === undefined || value === null || value === '') return undefined;
  const parts = Array.isArray(value)
    ? value.flatMap((item) => String(item).split(','))
    : String(value).split(',');
  const statuses = parts.map((item) => item.trim()).filter(Boolean);
  return statuses.length > 0 ? statuses : undefined;
}

export const productListQuerySchema = paginationQuerySchema.extend({
  search: z.string().max(200).optional(),
  categoryId: z.coerce.number().int().optional(),
  minScore: z.coerce.number().min(0).max(100).optional(),
  maxScore: z.coerce.number().min(0).max(100).optional(),
  severity: severitySchema.optional(),
  ruleId: z.string().max(80).optional(),
  hasPendingSuggestions: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
  sort: z.enum(['score', 'name', 'findings', 'updatedAt']).default('score'),
  order: z.enum(['asc', 'desc']).default('asc'),
});
export type ProductListQuery = z.infer<typeof productListQuerySchema>;

export interface ProductListItem {
  productId: number;
  name: string;
  brandName: string | null;
  categoryId: number | null;
  categoryName: string | null;
  /** Miniatura da imagem principal do snapshot; `null` se o produto não tem foto. */
  imageUrl: string | null;
  score: number;
  findingCount: number;
  criticalCount: number;
  pendingSuggestionCount: number;
  skuCount: number;
  fetchedAt: string;
}

export interface ProductDetail {
  productId: number;
  score: number;
  contentHash: string;
  fetchedAt: string;
  snapshot: ProductSnapshotData;
  findings: Array<Finding & { id: string }>;
  suggestions: Suggestion[];
}

export interface CatalogHealth {
  productCount: number;
  auditedCount: number;
  averageScore: number;
  scoreDistribution: Array<{ bucket: string; count: number }>;
  findingsByRule: Array<{ ruleId: string; severity: string; count: number; productCount: number }>;
  findingsBySeverity: Array<{ severity: string; count: number }>;
  fieldCoverage: Array<{ field: string; filled: number; missing: number; coverage: number }>;
  lastIngestionAt: string | null;
  lastAuditAt: string | null;
  pendingSuggestions: number;
  /** Produtos distintos com pelo menos uma sugestão pendente. */
  pendingProductCount: number;
}

export interface CategoryHealth {
  categoryId: number | null;
  categoryName: string;
  categoryPath: string[];
  productCount: number;
  averageScore: number;
  criticalCount: number;
  topRules: Array<{ ruleId: string; count: number }>;
}

/** Nó flattenado da árvore VTEX para picker de ingestão. */
export interface CatalogCategoryOption {
  id: number;
  name: string;
  /** Ancestral → folha (ex.: ["Moda", "Masculino"]). */
  path: string[];
}

/** Query do endpoint de impacto operacional do dashboard. */
export const impactQuerySchema = z.object({
  days: z
    .enum(['7', '30'])
    .default('30')
    .transform((value) => Number(value) as 7 | 30),
});
export type ImpactQuery = z.infer<typeof impactQuerySchema>;

/**
 * Métricas internas de impacto (revisão, publicação, lift de score) no período.
 * Não inclui GA4/ROI — só dados já persistidos no banco.
 */
export interface CatalogImpact {
  days: 7 | 30;
  periodStart: string;
  /** Sugestões com status `applied` atualizadas no período. */
  suggestionsApplied: number;
  /** Produtos distintos entre as sugestões aplicadas. */
  uniqueProductsEnriched: number;
  /** Decisões `approve` em `Review` no período. */
  approvedDecisions: number;
  /** Decisões `reject` em `Review` no período. */
  rejectedDecisions: number;
  /**
   * Taxa de aprovação: approved / (approved + rejected) × 100.
   * `0` quando não há decisões no período.
   */
  approvalRate: number;
  /** Campos mais publicados via writes succeeded. */
  topPublishedFields: Array<{ field: string; count: number }>;
  /** Total de campos em writes succeeded no período. */
  fieldsPublished: number;
  writeOpsSucceeded: number;
  /** Writes com status `failed` ou `aborted_conflict`. */
  writeOpsFailedOrConflict: number;
  /** Soma de `Suggestion.costUsd` criadas no período. */
  llmCostUsd: number;
  /** Total de reviews (approve + reject) no período. */
  reviewedCount: number;
  /** `reviewedCount × 3` — estimativa bruta de minutos equivalentes. */
  estimatedReviewMinutes: number;
  /**
   * Média de (scoreAfter − scoreBefore) em escritas succeeded com ambos preenchidos.
   * `null` quando não há escritas reauditadas no período.
   */
  averageScoreLift: number | null;
  /** Produtos distintos com lift de score > 0 (última escrita reauditada por produto). */
  productsWithPositiveLift: number;
  /** Produtos distintos com lift === 0. */
  productsWithNeutralLift: number;
  /** Produtos distintos com lift < 0. */
  productsWithNegativeLift: number;
  /**
   * Produtos com escrita succeeded no período, lift positivo e sem findings
   * críticos no snapshot atual (proxy de “críticos resolvidos” pós-reaudit).
   */
  criticalFindingsCleared: number;
  /** Escritas succeeded com scoreBefore e scoreAfter (base do lift). */
  scoreDeltasMeasured: number;
}

/** Formato do one-pager de impacto para download. */
export const impactExportQuerySchema = impactQuerySchema.extend({
  format: z.enum(['json', 'csv']).default('json'),
});
export type ImpactExportQuery = z.infer<typeof impactExportQuerySchema>;

export const suggestionListQuerySchema = paginationQuerySchema.extend({
  status: z.preprocess(
    preprocessStatusList,
    z.array(suggestionStatusSchema).min(1).optional(),
  ),
  field: enrichableFieldSchema.optional(),
  categoryId: z.coerce.number().int().optional(),
  productId: z.coerce.number().int().optional(),
  search: z.string().max(200).optional(),
});
/** Inferência de `preprocess` alarga o tipo; forçamos a saída esperada. */
export type SuggestionListQuery = Omit<
  z.infer<typeof suggestionListQuerySchema>,
  'status'
> & {
  status?: SuggestionStatus[];
};

export interface SuggestionLatestReview {
  decision: string;
  note: string | null;
  createdAt: string;
  userName: string;
}

export interface SuggestionListItem extends Suggestion {
  productName: string;
  categoryName: string | null;
  brandName: string | null;
  /** URL da imagem quando a sugestão é de imageAltText/imageLabel. */
  imageUrl?: string | null;
  /** Última decisão de revisão, se houver. */
  latestReview?: SuggestionLatestReview | null;
}

/** Fila de revisão agrupada: uma entrada por produto com todas as sugestões. */
export interface ProductSuggestionGroup {
  productId: number;
  productName: string;
  categoryName: string | null;
  brandName: string | null;
  /** Miniatura da imagem principal do snapshot; `null` se o produto não tem foto. */
  imageUrl: string | null;
  suggestions: SuggestionListItem[];
  /** Sugestões com validação determinística reprovada. */
  blockedCount: number;
}

export interface GroupedSuggestionList {
  items: ProductSuggestionGroup[];
  page: number;
  pageSize: number;
  /** Total de produtos (não de sugestões). */
  total: number;
  totalPages: number;
  /** Total de sugestões que batem no filtro (todas as páginas). */
  suggestionTotal: number;
}

export interface SuggestionStats {
  byStatus: Partial<Record<SuggestionStatus, number>>;
  /** Produtos distintos com pelo menos uma sugestão pendente. */
  pendingProductCount: number;
}

export const reviewDecisionSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  /** Texto final quando o revisor edita a sugestão antes de aprovar. */
  editedValue: z.string().max(20000).nullable().optional(),
  note: z.string().max(1000).optional(),
});
export type ReviewDecision = z.infer<typeof reviewDecisionSchema>;

export const bulkReviewSchema = z.object({
  suggestionIds: z.array(z.string().uuid()).min(1).max(500),
  decision: z.enum(['approve', 'reject']),
  note: z.string().max(1000).optional(),
});
export type BulkReview = z.infer<typeof bulkReviewSchema>;
